import {
  Facebook,
  Globe,
  Instagram,
  MapPin,
  MessageCircle,
  Phone,
  Send,
  ShieldCheck,
  Sparkles,
  Truck,
  Video
} from "lucide-react";
import Link from "next/link";

import { MovaLogo } from "@/components/ui/mova-logo";

type FooterLink = { label: string; href: string; external?: boolean };

const footerGroups: { title: string; links: FooterLink[] }[] = [
  {
    title: "About MOVA",
    links: [
      { label: "About MOVA Market", href: "/info/about-mova" },
      { label: "Our Quality Guarantee", href: "/info/about-mova" },
      { label: "Customer Reviews", href: "/search?sort=rating" },
      { label: "Careers & Join Us", href: "/info/careers" }
    ]
  },
  {
    title: "Customer Care",
    links: [
      { label: "Your Account", href: "/dashboard/profile" },
      { label: "Track Your Orders", href: "/dashboard/orders" },
      { label: "Shipping Rates & Policies", href: "/info/help" },
      { label: "Help & FAQ", href: "/info/help" }
    ]
  },
  {
    title: "Partner with Us",
    links: [
      { label: "Sell on MOVA Market", href: "/vendor" },
      { label: "Vendor Central Portal", href: "/vendor" },
      { label: "Affiliate & Accelerator", href: "/info/mova-accelerator" },
      { label: "Fulfilment by MOVA", href: "/info/fulfilment-by-mova" }
    ]
  },
  {
    title: "Official Channels",
    links: [
      { label: "Telegram Channel (t.me/movamarket)", href: "https://t.me/movamarket", external: true },
      { label: "Instagram (@mova_market21)", href: "https://instagram.com/mova_market21", external: true },
      { label: "TikTok (@movamarket4)", href: "https://tiktok.com/@movamarket4", external: true },
      { label: "Facebook Page", href: "https://facebook.com/profile.php?id=61594144354788", external: true }
    ]
  }
];

export function Footer() {
  return (
    <footer className="mt-12 border-t-2 border-[#00B7B0] bg-[#073B70] text-white">
      {/* Back to Top */}
      <button
        type="button"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className="block w-full bg-[#087DB5] py-3 text-center text-xs sm:text-sm font-bold tracking-wide text-white transition hover:bg-[#098dc9]"
      >
        ▲ Back to top
      </button>

      {/* 4 Core Trust Badges from Official Sticker */}
      <div className="border-b border-white/10 bg-[#06305C]">
        <div className="mx-auto max-w-[1500px] px-4 py-6 sm:px-6">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 sm:gap-6">
            {/* Badge 1: Quality Products */}
            <div className="flex items-center gap-3 rounded-xl bg-white/5 p-3 sm:p-4 border border-white/10">
              <div className="grid h-10 w-10 sm:h-12 sm:w-12 shrink-0 place-items-center rounded-full bg-[#087DB5] text-white shadow-md">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-white">Quality Products</h4>
                <p className="text-[11px] text-slate-300">We Don&apos;t Just Sell, We Guarantee Quality!</p>
              </div>
            </div>

            {/* Badge 2: Fast & Reliable Delivery */}
            <div className="flex items-center gap-3 rounded-xl bg-white/5 p-3 sm:p-4 border border-white/10">
              <div className="grid h-10 w-10 sm:h-12 sm:w-12 shrink-0 place-items-center rounded-full bg-[#00B7B0] text-[#073B70] shadow-md">
                <Truck className="h-6 w-6" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-white">Fast & Reliable Delivery</h4>
                <p className="text-[11px] text-slate-300">Prompt fulfillment across Addis Ababa & Ethiopia</p>
              </div>
            </div>

            {/* Badge 3: Great Prices */}
            <div className="flex items-center gap-3 rounded-xl bg-white/5 p-3 sm:p-4 border border-white/10">
              <div className="grid h-10 w-10 sm:h-12 sm:w-12 shrink-0 place-items-center rounded-full bg-[#FF8A00] text-white shadow-md">
                <Sparkles className="h-6 w-6" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-white">Great Prices</h4>
                <p className="text-[11px] text-slate-300">Exclusive online discounts & direct savings</p>
              </div>
            </div>

            {/* Badge 4: Secure Payments */}
            <div className="flex items-center gap-3 rounded-xl bg-white/5 p-3 sm:p-4 border border-white/10">
              <div className="grid h-10 w-10 sm:h-12 sm:w-12 shrink-0 place-items-center rounded-full bg-[#0A4E94] text-[#00B7B0] shadow-md">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-white">Secure Payments</h4>
                <p className="text-[11px] text-slate-300">Cash on Delivery & verified mobile pay</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Links */}
      <div className="mx-auto max-w-[1500px] px-4 py-8 sm:px-6 sm:py-12">
        <div className="grid grid-cols-2 gap-6 sm:grid-cols-4 lg:gap-8">
          {footerGroups.map((group) => (
            <div key={group.title}>
              <h3 className="mb-3.5 text-sm font-bold uppercase tracking-wider text-[#00B7B0]">{group.title}</h3>
              <ul className="space-y-2">
                {group.links.map((link) => (
                  <li key={link.label}>
                    {link.external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs sm:text-[13px] text-slate-200 hover:text-[#00B7B0] hover:underline transition-colors"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        href={link.href}
                        className="text-xs sm:text-[13px] text-slate-200 hover:text-[#00B7B0] hover:underline transition-colors"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Official Sticker Banner Strip: Order on Website & Direct Contacts */}
      <div className="border-t border-white/10 bg-[#05294F] py-8 px-4 sm:px-6">
        <div className="mx-auto max-w-[1500px] flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Logo & Tagline */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 text-center sm:text-left">
            <MovaLogo variant="white" size="lg" showTagline />
          </div>

          {/* Website Pill */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#00B7B0] bg-[#073B70] px-4 py-2 text-xs sm:text-sm font-bold text-white shadow-sm">
              <Globe className="h-4 w-4 text-[#00B7B0]" />
              <span>Shop on Our Website:</span>
              <span className="text-[#00B7B0]">mova-market.pro.et</span>
            </div>
          </div>

          {/* Social Media & Phone */}
          <div className="flex flex-col items-center md:items-end gap-2 text-center md:text-right">
            <div className="flex items-center gap-2">
              <a
                href="https://t.me/movamarket"
                target="_blank"
                rel="noopener noreferrer"
                className="grid h-8 w-8 place-items-center rounded-full bg-white/10 text-white hover:bg-[#00B7B0] hover:text-[#073B70] transition-colors"
                title="Telegram Channel"
              >
                <Send className="h-4 w-4" />
              </a>
              <a
                href="https://instagram.com/mova_market21"
                target="_blank"
                rel="noopener noreferrer"
                className="grid h-8 w-8 place-items-center rounded-full bg-white/10 text-white hover:bg-[#00B7B0] hover:text-[#073B70] transition-colors"
                title="Instagram @mova_market21"
              >
                <Instagram className="h-4 w-4" />
              </a>
              <a
                href="https://facebook.com/profile.php?id=61594144354788"
                target="_blank"
                rel="noopener noreferrer"
                className="grid h-8 w-8 place-items-center rounded-full bg-white/10 text-white hover:bg-[#00B7B0] hover:text-[#073B70] transition-colors"
                title="Facebook"
              >
                <Facebook className="h-4 w-4" />
              </a>
              <a
                href="https://tiktok.com/@movamarket4"
                target="_blank"
                rel="noopener noreferrer"
                className="grid h-8 w-8 place-items-center rounded-full bg-white/10 text-white hover:bg-[#00B7B0] hover:text-[#073B70] transition-colors"
                title="TikTok @movamarket4"
              >
                <Video className="h-4 w-4" />
              </a>
            </div>

            <div className="flex flex-wrap items-center justify-center md:justify-end gap-x-2 gap-y-1 text-xs sm:text-sm font-bold text-white">
              <span className="flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5 text-[#00B7B0]" />
                Contact:
              </span>
              <a
                href="tel:+251931463406"
                className="hover:text-[#00B7B0] transition-colors hover:underline"
              >
                +251 93 146 3406
              </a>
              <span className="text-white/40">/</span>
              <a
                href="tel:+251995871028"
                className="hover:text-[#00B7B0] transition-colors hover:underline"
              >
                +251 99 587 1028
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright & Legal */}
      <div className="bg-[#031B36] py-5 px-4 text-center text-[11px] sm:text-xs text-slate-400">
        <div className="mb-2 flex flex-wrap justify-center gap-x-5 gap-y-1">
          <Link href="/info/about-mova" className="hover:text-white transition-colors">About MOVA</Link>
          <Link href="/info/privacy-notice" className="hover:text-white transition-colors">Privacy Notice</Link>
          <Link href="/info/conditions-of-use" className="hover:text-white transition-colors">Terms of Service</Link>
          <Link href="/info/help" className="hover:text-white transition-colors">Help Center</Link>
        </div>
        <p>
          &copy; {new Date().getFullYear()} MOVA MARKET ETH. All rights reserved. &bull; Official Ethiopian E-Commerce Platform
        </p>
      </div>
    </footer>
  );
}
